import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { LosLogo } from "@/components/Logo";
import { QuoteDocument } from "@/components/canvas/QuoteDocument";

/** Customer-facing quote. Read-only projection - the API never sends cost or margin here. */
export default function PublicQuotePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = "Proposal – LOS Marine";
    api.get(`/public/quotes/${token}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.quote?.title || "Proposal"} – LOS Marine`;
    }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const respond = async () => {
    if (!name.trim()) return toast("Please enter your name");
    setBusy(true);
    try {
      await api.post(`/public/quotes/${token}/respond`, { action: mode, message: msg, name });
      setDone(mode);
      setMode(null);
      toast.success(mode === "accept" ? "Thank you – the proposal is accepted" : "Thank you – your message has been sent");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6" data-testid="public-quote-error">
        <div className="max-w-md rounded-[20px] border border-black/[0.07] bg-white p-10 text-center shadow-float">
          <LosLogo className="justify-center text-[18px]" />
          <h1 className="mt-8 font-display text-[22px] font-semibold tracking-[-0.02em]">This proposal link is not valid</h1>
          <p className="mt-3 text-[14px] leading-6 text-muted-foreground">{error}. Please contact your LOS Marine representative for an updated link.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground" data-testid="public-quote-loading"><span className="mr-2 h-2 w-2 rounded-full bg-primary pulse-dot" /> Loading proposal</div>;

  const q = data.quote;
  const accepted = q.customer_accepted_at || done === "accept";
  const lang = q.language === "no" ? "no" : "en";
  const T = lang === "no" ? { accept: "Aksepter tilbudet", question: "Still et spørsmål", pdf: "PDF", accepted: "Tilbudet er akseptert", by: "av", send: "Send", name: "Navn", message: "Melding", cancel: "Avbryt", shared: "Delt av" } : { accept: "Accept proposal", question: "Ask a question", pdf: "PDF", accepted: "Proposal accepted", by: "by", send: "Send", name: "Your name", message: "Message", cancel: "Cancel", shared: "Shared by" };

  return (
    <div className="min-h-screen bg-background text-foreground print:bg-white" data-testid="public-quote-page">
      <div className="no-print sticky top-0 z-30 bg-[hsl(var(--background)/0.86)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1000px] items-center justify-between gap-4 px-4 sm:px-8">
          <LosLogo className="text-[17px]" />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => window.print()} className="hidden h-9 items-center gap-1.5 rounded-full border border-black/[0.09] bg-white px-3.5 text-[12.5px] font-medium hover:border-black/[0.2] sm:inline-flex" data-testid="public-quote-pdf-button"><Printer className="h-3.5 w-3.5" /> {T.pdf}</button>
            {!accepted ? (
              <>
                <button type="button" onClick={() => setMode("question")} className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12.5px] font-medium text-muted-foreground hover:text-foreground" data-testid="public-quote-question-button"><MessageSquare className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{T.question}</span></button>
                <button type="button" onClick={() => setMode("accept")} className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 text-[12.5px] font-semibold text-white" data-testid="public-quote-accept-button"><Check className="h-3.5 w-3.5" /> {T.accept}</button>
              </>
            ) : (
              <span className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[hsl(var(--v-ok-bg))] px-3.5 text-[12.5px] font-semibold text-[hsl(var(--v-ok))]" data-testid="public-quote-accepted"><Check className="h-3.5 w-3.5" /> {T.accepted}{q.customer_accepted_by ? ` ${T.by} ${q.customer_accepted_by}` : ""}</span>
            )}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1000px] px-4 pb-20 pt-6 sm:px-8 print:max-w-none print:p-0">
        <div className="print-page rounded-[22px] border border-black/[0.06] bg-white px-6 py-10 shadow-float sm:px-14 sm:py-14">
          <QuoteDocument quote={q} company={data.company} customer={data.account} owner={data.owner} testId="public-quote-document" />
        </div>
        {mode ? (
          <div className="no-print mx-auto mt-6 max-w-[620px] rounded-[18px] border border-black/[0.07] bg-white p-6 shadow-float" data-testid="public-quote-respond">
            <div className="text-[15px] font-semibold">{mode === "accept" ? T.accept : T.question}</div>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={T.name} className="mt-4 h-11 w-full rounded-lg border border-black/[0.1] px-3 text-[14px] outline-none focus:border-primary/60" data-testid="public-quote-name-input" />
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder={T.message} className="mt-3 w-full resize-none rounded-lg border border-black/[0.1] px-3 py-2.5 text-[14px] outline-none focus:border-primary/60" data-testid="public-quote-message-input" />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setMode(null)} className="h-10 rounded-full px-4 text-[13px] font-medium text-muted-foreground hover:text-foreground">{T.cancel}</button>
              <button type="button" disabled={busy} onClick={respond} className={cn("h-10 rounded-full bg-[hsl(var(--ink))] px-5 text-[13px] font-semibold text-white hover:bg-[hsl(var(--ink-2))] disabled:opacity-40")} data-testid="public-quote-respond-submit">{T.send}</button>
            </div>
          </div>
        ) : null}
        <p className="no-print mt-8 text-center text-[12px] text-muted-foreground">{T.shared} {data.owner?.name} · LOS Marine AS, Rubbestadneset</p>
      </main>
    </div>
  );
}
